/**
 * Store Zustand pour la gestion de l'authentification
 * VERSION CORRIGEE P0 — Compatible cookies httpOnly
 *
 * VULN-001 CORRIGE :
 * - Plus de stockage localStorage pour les tokens
 * - Plus de persist middleware
 * - Tokens geres exclusivement par cookies httpOnly (backend)
 * - Le store garde uniquement : user, permissions, etat auth
 */

import { create } from 'zustand'
import { authService } from '../services/authService'
import type { User } from '../types'

interface ExtendedUser extends User {
  school_id?: number
  academic_year_id?: number
}

interface AuthState {
  user: ExtendedUser | null
  isAuthenticated: boolean
  isLoading: boolean

  permissions: string[]
  role: string
  roleName: string
  isAdmin: boolean
  isSuperAdmin: boolean

  setUser: (user: ExtendedUser | null) => void
  login: (user: ExtendedUser) => void
  logout: () => Promise<void>
  setLoading: (loading: boolean) => void
  checkAuth: () => Promise<boolean>

  setPermissions: (permissions: string[], role: string, roleName: string, isAdmin: boolean, isSuperAdmin: boolean) => void
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasAllPermissions: (permissions: string[]) => boolean
}

const FULL_ACCESS_ROLES = ['super_admin', 'admin', 'proviseur']

export const useAuthStore = create<AuthState>()(
  (set, get) => ({
    user: null,
    isAuthenticated: false,
    isLoading: false,

    permissions: [],
    role: '',
    roleName: '',
    isAdmin: false,
    isSuperAdmin: false,

    setUser: (user) => set({ user }),

    login: (user) =>
      set({
        user,
        isAuthenticated: true,
        isLoading: false,
      }),

    logout: async () => {
      try {
        await authService.logout()
      } catch (e) {
        console.warn('Erreur lors du logout backend:', e)
      }
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        permissions: [],
        role: '',
        roleName: '',
        isAdmin: false,
        isSuperAdmin: false,
      })
    },

    setLoading: (loading) => set({ isLoading: loading }),

    checkAuth: async () => {
      set({ isLoading: true })
      try {
        const user = await authService.getCurrentUser()
        const perms = await authService.getUserPermissions()

        set({
          user: user as ExtendedUser,
          isAuthenticated: true,
          permissions: perms.permissions,
          role: perms.role,
          roleName: perms.role_name,
          isAdmin: perms.is_admin,
          isSuperAdmin: perms.is_super_admin,
          isLoading: false,
        })
        return true
      } catch (error) {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          permissions: [],
          role: '',
          roleName: '',
          isAdmin: false,
          isSuperAdmin: false,
        })
        return false
      }
    },

    setPermissions: (permissions, role, roleName, isAdmin, isSuperAdmin) =>
      set({ permissions, role, roleName, isAdmin, isSuperAdmin }),

    hasPermission: (permission) => {
      const state = get()
      if (FULL_ACCESS_ROLES.includes(state.role)) return true
      if (state.isSuperAdmin) return true
      return state.permissions.includes(permission) || state.permissions.includes('admin_full')
    },

    hasAnyPermission: (permissions) => {
      const state = get()
      if (FULL_ACCESS_ROLES.includes(state.role)) return true
      if (state.isSuperAdmin) return true
      return permissions.some(p => state.permissions.includes(p) || p === 'admin_full')
    },

    hasAllPermissions: (permissions) => {
      const state = get()
      if (FULL_ACCESS_ROLES.includes(state.role)) return true
      if (state.isSuperAdmin) return true
      return permissions.every(p => state.permissions.includes(p) || p === 'admin_full')
    },
  })
)